import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Menu from "../Menu";
import useApiUrls from "../../useApiUrls";
import Pagination from "../components/Pagination";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";

const DefaultHtml = () => {
  const [data, setData] = useState([]);
  const [dataLimit, setDataLimit] = useState(10);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [noDataMessage, setNoDataMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const token = getCookie("token");
  const userid = getCookie("userid") || "1";

  const fetchDefaultHtmlData = async () => {
    setLoading(true);
    try {
      const searchUrl =
        getUrlByOperation("DefaultHtmlSearch") ||
        "https://api.dqr-display-tms.eventum.co.in/api/DefaultHtmlOperation/Search";

      const requestBody = {
        id: -2,
        groupid: -2,
        subgroupid: -2,
        htmldocid: -2,
        htmldoczipid: -2,
        createdip: "",
        modifiedip: "",
        applycreateddate: true,
        applymodifieddate: true,
        fromcreateddate: 0,
        endcreateddate: 0,
        frommodifieddate: 0,
        endmodifieddate: 0,
        ispageload: true,
        pagesize: dataLimit || 1000,
        currentpageno: count,
        withoutpagination: true,
        pagedirection: "",
      };

      const response = await axios.post(searchUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
          accept: "text/plain",
        },
      });

      if (response.data && response.data.isresponse && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const rows = response.data.data;
        setData(rows);
        setHasMore(rows.length === Number(dataLimit));
        const approximateTotal =
          count * Number(dataLimit) + rows.length + (rows.length === Number(dataLimit) ? Number(dataLimit) : 0);
        setTotalItems(approximateTotal);
        setNoDataMessage("");
      } else {
        setData([]);
        setHasMore(false);
        setTotalItems(0);
        const errMsg = response.data?.errormsg || response.data?.message || "No matching records found";
        setNoDataMessage(errMsg);
      }
    } catch (error) {
      console.error("Error fetching Default HTML data:", error);
      setData([]);
      setHasMore(false);
      setTotalItems(0);
      const errMsg =
        error.response?.data?.errormsg ||
        error.response?.data?.message ||
        error.message ||
        "No matching records found";
      setNoDataMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDefaultHtmlData();
  }, [getUrlByOperation, token, userid, dataLimit, count]);

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const handleAddClick = () => {
    navigate("/add-default-html");
  };

  const handleEdit = (id) => {
    navigate(`/update-default-html/${id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Default HTML entry?")) {
      return;
    }

    try {
      const deleteUrl =
        getUrlByOperation("DefaultHtmlDelete") ||
        "https://api.dqr-display-tms.eventum.co.in/api/DefaultHtmlOperation/Delete";

      const response = await axios.post(
        deleteUrl,
        { id: parseInt(id) },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (
        response.data &&
        (response.data.isresponse === true || response.data.responsestatus === "SUCCESS")
      ) {
        setMessage(response.data?.errormsg || "Default HTML entry deleted successfully!");
        setMessageType("success");
      } else {
        const errStr =
          response.data?.errormsg ||
          response.data?.message ||
          "Failed to delete Default HTML entry.";
        setMessage(errStr);
        setMessageType("error");
      }
      fetchDefaultHtmlData();
    } catch (error) {
      console.error("Error deleting item:", error);
      const errStr =
        error.response?.data?.errormsg ||
        error.response?.data?.message ||
        error.message ||
        "Failed to delete item.";
      setMessage(errStr);
      setMessageType("error");
    }
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1>Default HTML</h1>
                </div>
                <div className="col-sm-6">
                  <button
                    className="btn btn-info float-sm-right"
                    onClick={handleAddClick}
                  >
                    + Add Default HTML
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">Default HTML List</h3>
                    </div>
                    <div className="card-body table-responsive p-0">
                      {loading ? (
                        <div className="text-center p-4">Loading...</div>
                      ) : data && data.length > 0 ? (
                        <table className="table table-hover text-nowrap">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Group Name</th>
                              <th>SubGroup Name</th>
                              <th>HTML Document</th>
                              <th>HTML Doc Zip</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.map((item, index) => (
                              <tr key={item.id || index}>
                                <td>{item.id || index + 1}</td>
                                <td>{item.groupid === -1 ? "All" : item.groupname || item.groupid || "-"}</td>
                                <td>{item.subgroupid === -1 ? "All" : item.subgroupname || item.subgroupid || "-"}</td>
                                <td>{item.htmldocname || item.htmldocidname || (item.htmldocid && item.htmldocid > 0 ? item.htmldocid : "-")}</td>
                                <td>{item.htmldoczipname || item.htmldoczipidname || (item.htmldoczipid && item.htmldoczipid > 0 ? item.htmldoczipid : "-")}</td>
                                <td>
                                  {item.id && (
                                    <>
                                      <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => handleEdit(item.id)}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        className="btn btn-danger btn-sm ml-1"
                                        onClick={() => handleDelete(item.id)}
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="text-center p-4 text-muted font-weight-bold">
                          {noDataMessage || "No matching records found"}
                        </div>
                      )}
                    </div>
                    {data && data.length > 0 && (
                      <div className="card-footer clearfix">
                        <Pagination
                          currentPage={count + 1}
                          hasMore={hasMore}
                          onPageChange={handlePageChange}
                          totalItems={totalItems}
                          itemsPerPage={dataLimit}
                          onItemsPerPageChange={handleItemsPerPageChange}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {message && messageType === "success" && (
        <SuccessMessage message={message} onClose={() => setMessage("")} />
      )}
      {message && messageType === "error" && (
        <FailedMessage message={message} onClose={() => setMessage("")} />
      )}
    </>
  );
};

export default DefaultHtml;
